import React from "react";

/**
 * Plain native radio inputs for react-final-form.
 *
 * Use with `<Field component={RadioField} />`. Unlike `RadioButtons`, this renders
 * unstyled `<input type="radio">` elements — one per option, stacked vertically.
 *
 * @example
 * const OPTIONS = [
 *   { value: "detailer_estimated_date", label: "Detailer Estimated Date" },
 *   { value: "required_project_date", label: "Required Project Date" },
 * ];
 *
 * <Field
 *   component={RadioField}
 *   name="ifa_date_set_by"
 *   options={OPTIONS}
 *   validate={required}
 *   className="mt-2"
 * />
 *
 * @param {object} props
 * @param {object} props.input - react-final-form input (`name`, `value`, `onChange`, `onBlur`)
 * @param {object} props.meta - react-final-form meta (`touched`, `error`, `warning`)
 * @param {{ value: string, label: string }[]} [props.options=[]] - choices to render
 * @param {string} [props.className] - wrapper class (e.g. spacing)
 * @param {string} [props.optionClassName] - class applied to each option row
 * @param {string} [props.label] - label prefix shown in validation errors
 * @param {string} [props.errorName] - fallback name shown in validation errors when `label` is omitted
 * @param {boolean} [props.disabled] - disable all radio inputs
 */
const RadioField = (props) => {
  const {
    input: { value, onChange, onBlur, name },
    meta: { touched, error, warning },
    options = [],
    className,
    optionClassName,
    errorName,
    label,
    disabled,
  } = props;

  return (
    <div className={className}>
      {options.map((option) => (
        <div key={option.value} className={optionClassName}>
          <label>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              onBlur={onBlur}
              disabled={disabled}
            />{" "}
            {option.label}
          </label>
        </div>
      ))}
      {touched &&
        ((error && (
          <span className="text-danger">
            {label || errorName || name} {error}
          </span>
        )) ||
          (warning && <span className="text-warning">{warning}</span>))}
    </div>
  );
};

export default RadioField;
